import { FormatAmountOptions, FormatCurrencyOptions } from 'types/format.ts'
import { CustomerInvoice, LANGUAGES } from '@boltenergy-be/shared'

/**
 * Capitalizes the first letter of a given text
 * @param {string} text
 * @returns {string}
 */
export const capitalizeFirstLetter = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * Formats a given amount to a nl locale string with 2 decimals or the given fraction digits.
 * Each option can be given on its own: the one that isn't given falls back to a value that
 * never violates the min <= max constraint Intl requires.
 *
 * Returns an empty string when there is no number to format, like formatCurrency does.
 *
 * @param {number | null | undefined} num
 * @param {FormatAmountOptions} [options]
 * @returns {string}
 */
export const formatAmount = (num: number | null | undefined, options?: FormatAmountOptions): string => {
  if (typeof num !== 'number' || isNaN(num)) return ''

  const fallback = options?.fractionDigits ?? 2
  const minimumFractionDigits = options?.minFractionDigits ?? Math.min(fallback, options?.maxFractionDigits ?? fallback)
  const maximumFractionDigits = options?.maxFractionDigits ?? Math.max(fallback, minimumFractionDigits)

  return num.toLocaleString('nl-BE', { maximumFractionDigits, minimumFractionDigits })
}

/**
 * Formats a given amount as currency with the given options.
 * Removes ",00" on round currencies like € 100,00.
 *
 * @param {number} amount
 * @param {FormatCurrencyOptions} options
 * @returns {string}
 */
export const formatCurrency = (amount: number, options?: FormatCurrencyOptions): string => {
  const fractionDigits = options?.maximumFractionDigits ?? 2
  const intlNumberFormat = Intl.NumberFormat(options?.language ?? LANGUAGES.NL, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: fractionDigits
  })
  const digitsRegex = new RegExp(`,0{${fractionDigits}}`)
  return isNaN(amount) ? '' : intlNumberFormat.format(amount).replace(digitsRegex, '')
}

/**
 * Formats a given number to a nl locale string with 2 decimals
 *
 * @deprecated Use formatAmount instead
 * @param {number} num
 * @param {boolean=} hasDecimals
 * @returns {string}
 */
export const formatNumber = (num: number, hasDecimals?: boolean): string => {
  const formatted = !hasDecimals ? Math.round(num) : Math.round((num + Number.EPSILON) * 100) / 100

  return formatted
    .toString()
    .replace(/\./g, ',')
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
}

/**
 *  Mask the data with asterisks
 *
 * @param {string | number | null} [data]
 * @param {number} [maskOffset]
 * @returns {string}
 */
export const maskWithAsterisk = (data?: string | number | null, maskOffset: number = 4): string => {
  if (!data) return ''
  return data.toString().replace(/.(?=.{0,}$)/g, (match, offset) => (offset < maskOffset ? match : '*'))
}

/**
 * Removes all special characters and converts to lowercase
 * @param {string} text
 * @returns string
 */
export const normalizeText = (text: string): string => {
  return text
    ? text
        .normalize('NFD') // separate accents from letters and return Unicode codes
        .replace(/[\u0300-\u036f]/g, '') // replace diacritical characters with empty string
        .toLowerCase()
    : ''
}

/**
 * Removes spaces and dots in the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const removeSpacesAndDots = (text: string): string => {
  // \s+ matches all whitespace characters, including spaces, non-break spaces, tabs, new lines and carriage returns
  const noSpaces = text.replace(/\s+/g, '')
  return noSpaces.replace(/\./g, '')
}

/**
 * Removes all non-number characters in the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const removeNonNumbers = (text: string): string => {
  return text.replace(/[^0-9]/g, '')
}

/**
 * Removes all non-alphanumeric characters in the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const removeNonAlphanumericals = (text: string): string => {
  return text.replace(/[^a-zA-Z0-9]/g, '')
}

/**
 * Replaces all spaces in the given string & returns it
 * @param {string} text
 * @returns {string}
 */
export const capitalize = (text?: string | null): string | undefined => {
  if (!text) return ''
  return text.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase())
}

/**
 * Converts a base64 string to a blob
 *
 * @param {string} data
 * @param {string} type
 * @returns {Blob}
 */
export const convertBase64toBlob = (data: string, type: string) => {
  const bytes = atob(data)
  let length = bytes.length
  const out = new Uint8Array(length)

  while (length--) {
    out[length] = bytes.charCodeAt(length)
  }

  return new Blob([out], { type })
}

/**
 * Formats the invoice download url to include the correct file name when downloaded
 * @param {CustomerInvoice} invoice
 * @param {"pdf" | "xml"} format
 * @returns {string}
 */
export const formatInvoiceDownloadUrl = (invoice: CustomerInvoice, format: 'pdf' | 'xml'): string => {
  return `${format === 'xml' ? invoice.peppolUrl : invoice.pdfUrl}?name=bolt-${invoice.invoiceNumber}.${format}`
}
